import {
  Button,
  Divider,
  Flex,
  Form,
  Row,
  Spin,
  Tooltip,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { useAdminBobAccountController_findAll } from '@api-client/generated/AdminBobAccountController/findAll';
import { useAdminBobDaybookController_findAll } from '@api-client/generated/AdminBobDaybookController/findAll';
import { useAdminBobVATController_findAll } from '@api-client/generated/AdminBobVATController/findAll';
import { useAdminCurrencyExchangeRateController_getRate } from '@api-client/generated/AdminCurrencyExchangeRateController/getRate';
import { useAdminDocumentController_findLastParamsForContact } from '@api-client/generated/AdminDocumentController/findLastParamsForContact';
import { useAdminDocumentController_retryBobSync } from '@api-client/generated/AdminDocumentController/retryBobSync';
import { useAdminDocumentController_updateOneById } from '@api-client/generated/AdminDocumentController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { IconEdit, IconLock, IconTrash } from '@assets';
import {
  DEFAULT_CURRENCY_CODE,
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIMEOUT_FOR_DEBOUNCE,
  currencyCodes,
} from '@constants';
import {
  ContactsPopupList,
  DocumentExpenseActionButtonWithConfirmation,
  DocumentExpenseAmountStatus,
  DocumentExpenseWrapperFormControl,
} from '@entities';
import { useCurrentCompany, useCurrentCompanyId } from '@hooks';
import { captureException } from '@sentry/react';
import { getRetryEligibility } from '@utils';

import {
  calculateDocumentEurAmount,
  getStatusByAmount,
  getStatusByEurAmount,
  normalizeBookingItems,
  resetEurValues,
  updateEurTotalPriceByIndex,
  updateItemEurNetPriceByIndex,
  updateItemEurTaxAmountByIndex,
  updateItemTaxAmountByIndex,
  updateItemTotalPriceByIndex,
} from './helpers';
import * as S from './styled';
import {
  BookingDocumentMetadata,
  BookkeepingDocumentMetadataDto,
} from './types';

type DocumentExpenseFormProps = {
  document: Schemas.Document;
  onAfterUpdate: () => void;
  /**
   * Notifies the parent whether the form has changes that are still being
   * persisted (a debounced auto-save is scheduled or its request is in
   * flight). Lets page-level actions (e.g. "Mark as Booked") stay disabled
   * until edits are saved, so a refetch can't wipe not-yet-saved input.
   */
  onPendingChange?: (pending: boolean) => void;
};

type UnlockedFieldsType = {
  taxAmount: Record<number, boolean>;
  eurNetPrice: Record<number, boolean>;
  eurTaxAmount: Record<number, boolean>;
};

const showUpdatedMessage = () =>
  message.success('Document information has been successfully updated');

const DocumentExpenseForm: FC<DocumentExpenseFormProps> = ({
  document,
  onAfterUpdate,
  onPendingChange,
}) => {
  const { id: documentId } = useParams();
  const companyId = useCurrentCompanyId();
  const { data: company, isPending: companyLoading } = useCurrentCompany();
  const hasBobReferenceId = company?.bobReferenceId;

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const isProcessing = document.adminStatus === 'processing';
  const isBooked = document.adminStatus === 'processed';
  const isReady = document.adminStatus === 'ready';
  const hasBookingError = document.bobStatus === 'failed';
  const isProcessingManually =
    isProcessing &&
    (document.bobStatus === null || document.bobStatus === undefined);
  // A document sitting in the BOB queue used to have no action at all: the form is read-only while it
  // is `processing`, and the two buttons below were shown only for a manual booking. So a stuck
  // document could be neither fixed nor withdrawn from here — only through a raw API call [ENG-1261].
  const isQueuedForBob = isProcessing && document.bobStatus === 'pending';
  const canLeaveProcessing = isProcessingManually || isQueuedForBob;
  // `undefined` while the company is still loading, not `false`: the rule only narrows, and collapsing
  // "not known yet" into "no dossier" would block an eligible retry for as long as the request is in
  // flight — with a reason that is not true.
  const retry = getRetryEligibility(
    document,
    companyLoading ? undefined : Boolean(hasBobReferenceId)
  );

  const hasDisabled = isBooked || isProcessing;

  const currency = form.getFieldValue('currency');
  const currencyExistsAndIsNotDefault =
    currency !== undefined &&
    currency !== null &&
    currency !== DEFAULT_CURRENCY_CODE;

  const reqParameters = {
    id: documentId!,
    companyId: companyId!,
  };

  const [paramsForContact, setParamsForContact] =
    useState<Schemas.BobLastDataForContactResponseDto>();
  const [submittable, setSubmittable] = useState(false);
  const [formValues, setFormValues] = useState<
    BookkeepingDocumentMetadataDto | BookingDocumentMetadata
  >();
  const [selectedContact, setSelectedContact] =
    useState<Schemas.BobContact | null>(null);
  const [unlockedDocumentId, setUnlockedDocumentId] = useState(false);
  const [unlockedEurAmount, setUnlockedEurAmount] = useState(false);
  // Number of metadata PATCHes currently in flight. A counter (not a boolean)
  // so two overlapping saves can't have the faster one clear the flag while the
  // slower is still pending — which would briefly (and wrongly) enable booking.
  const [pendingSaveCount, setPendingSaveCount] = useState(0);
  // True while a debounced auto-save is scheduled but its request hasn't fired
  // yet, so the gap before the PATCH also counts as "not yet persisted".
  const [isDebouncedSavePending, setIsDebouncedSavePending] = useState(false);
  // The form holds changes that are not yet persisted on the server while a
  // save is scheduled or any save request is still in flight.
  const isSavingChanges = pendingSaveCount > 0 || isDebouncedSavePending;

  // NOTE: Example of the state structure for unlockedItemsFields:
  // eurNetPrice: { 0: false, 2: true }, // Item at index 0 has locked EUR net price, item at index 2 has unlocked EUR net price
  const [unlockedItemsFields, setUnlockedItemsFields] =
    useState<UnlockedFieldsType>({
      taxAmount: {},
      eurNetPrice: {},
      eurTaxAmount: {},
    });

  const {
    data: currencyExchangeRateData,
    isFetching: isLoadingCurrencyExchangeRate,
  } = useAdminCurrencyExchangeRateController_getRate({
    params: {
      from: DEFAULT_CURRENCY_CODE,
      to: form.getFieldValue('currency'),
      date: dayjs(form.getFieldValue('issueDate')).format('YYYY-MM-DD'),
    },
    config: {
      refetchOnWindowFocus: false,
      enabled: !!(
        form.getFieldValue('currency') && form.getFieldValue('issueDate')
      ),
    },
  });
  const currencyExchangeRate = currencyExchangeRateData?.rate;

  const { data: daybooks, isPending: isLoadingDaybooks } =
    useAdminBobDaybookController_findAll({
      params: {
        companyId,
      },
    });

  const { data: accounts, isPending: isLoadingAccounts } =
    useAdminBobAccountController_findAll({
      params: {
        companyId,
      },
    });

  const { data: vats, isPending: isLoadingVats } =
    useAdminBobVATController_findAll({
      params: {
        companyId,
      },
    });

  const { refetch, isFetching: isLoadingParamsForContact } =
    useAdminDocumentController_findLastParamsForContact({
      params: {
        companyId: companyId!,
        contactId: selectedContact?.id as string,
      },
      config: {
        enabled: false,
      },
    });

  const { mutate: updateDocument } = useAdminDocumentController_updateOneById();
  const { mutate: retryBobSync, isPending: isRetrying } =
    useAdminDocumentController_retryBobSync();

  useEffect(() => {
    if (paramsForContact === undefined) return;

    const formItems = form.getFieldValue('items');

    if (isEmpty(paramsForContact)) {
      const bodyParams = {
        bobContactId: form.getFieldValue('bobContactId'),
        daybookId: null,
        items: formItems.map((item: Schemas.BookingItem) => ({
          ...item,
          accountCode: null,
          vatCode: null,
          isEffectiveVatZero: null,
          totalPrice: null,
          taxAmount: null,
          eurTotalPrice: null,
          eurTaxAmount: null,
        })),
      };

      handleUpdate(bodyParams);
      return;
    }

    const bodyParams = {
      bobContactId: form.getFieldValue('bobContactId'),
      daybookId: paramsForContact.daybookId,
      items:
        formItems.length === 1
          ? handleUpdateItemTaxFieldsbyIndex(
              0,
              formItems.map((item: Schemas.BookingItem) => ({
                ...item,
                accountCode: paramsForContact.accountCode,
                vatCode: paramsForContact.vatCode,
                isEffectiveVatZero: paramsForContact.isEffectiveVatZero,
              }))
            )
          : formItems,
    };

    handleUpdate(bodyParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsForContact]);

  useEffect(
    () => {
      if (document) {
        updateFormFields(document);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [document]
  );

  useEffect(
    () => {
      if (hasDisabled) return; // NOTE: We don't need to update the document currency-/exchange rate- related fields if it's in 'In Work' or 'Booked' statuses

      const currency = form.getFieldValue('currency');
      const issueDate = form.getFieldValue('issueDate');
      if (
        currency === undefined ||
        currency === null ||
        currency === DEFAULT_CURRENCY_CODE
      )
        return;
      if (issueDate === undefined || issueDate === null) return;

      let updatedDocumentValues: BookkeepingDocumentMetadataDto = {};
      let itemsToUpdate = form.getFieldValue('items') || [];

      // NOTE: Look for the // NOTE in the currency and issueDate form fields for the explanation
      updatedDocumentValues.currency = currency;
      updatedDocumentValues.issueDate = dayjs(issueDate).format('YYYY-MM-DD');

      const fromEurRate = form.getFieldValue('fromEurRate');

      const isExchangeRateUndefined = currencyExchangeRate === undefined; // NOTE: We didn't receive the exchange rate from the API
      const isExchangeRateNull = currencyExchangeRate === null; // NOTE: We received the exchange rate from the API, but it's null (there's no exchange rate for the selected date in the database)
      const isExchangeRateExistsAndChanged =
        !isExchangeRateUndefined &&
        !isExchangeRateNull &&
        currencyExchangeRate !== fromEurRate;

      if (isExchangeRateExistsAndChanged) {
        updatedDocumentValues.fromEurRate = currencyExchangeRate;

        for (let i = 0; i < itemsToUpdate.length; i++) {
          itemsToUpdate = updateItemEurNetPriceByIndex(
            i,
            itemsToUpdate,
            currency,
            currencyExchangeRate
          );
          itemsToUpdate = updateItemEurTaxAmountByIndex(
            i,
            itemsToUpdate,
            currency,
            currencyExchangeRate
          );
          itemsToUpdate = updateEurTotalPriceByIndex(i, itemsToUpdate);
        }

        const amount = form.getFieldValue('amount');
        updatedDocumentValues.eurAmount = calculateDocumentEurAmount(
          amount,
          currency,
          currencyExchangeRate
        );

        updatedDocumentValues.items = itemsToUpdate;
      } else if (isExchangeRateNull) {
        updatedDocumentValues = resetEurValues(
          updatedDocumentValues,
          itemsToUpdate
        );
      }
      handleUpdateWithDebounce(updatedDocumentValues);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencyExchangeRate]
  );

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  useEffect(() => {
    onPendingChange?.(isSavingChanges);
  }, [isSavingChanges, onPendingChange]);

  useEffect(
    () => () => {
      form.resetFields();
      setParamsForContact(undefined);
    },
    [form]
  );

  const updateFormFields = (document: Schemas.Document) => {
    // `items` can arrive as a non-array from a malformed/legacy `bookeeping_metadata`
    // row; coerce it before it reaches the form store and <Form.List>, both of which
    // assume an array. See `normalizeBookingItems`.
    const bookeepingMetadata = {
      ...document.bookeepingMetadata,
      items: normalizeBookingItems(document.bookeepingMetadata?.items),
    };

    form.setFieldsValue({
      ...bookeepingMetadata,
      bobContactId: document.bobContact?.id,
      referenceId: document.referenceId,
      issueDate:
        bookeepingMetadata.issueDate && dayjs(bookeepingMetadata.issueDate),
      dueDate: bookeepingMetadata.dueDate && dayjs(bookeepingMetadata.dueDate),
    });

    setSelectedContact(document.bobContact);
    setFormValues(bookeepingMetadata);
  };

  const debouncedUpdate = useDebouncedCallback((values) => {
    // The scheduled save is firing now; handleUpdate takes over tracking via
    // the in-flight counter, so clear the "scheduled" flag in the same tick to
    // avoid a gap where isSavingChanges would flip to false.
    setIsDebouncedSavePending(false);
    handleUpdate(values);
  }, DEFAULT_TIMEOUT_FOR_DEBOUNCE);

  // Keeps the existing call-site name (and its parameter type) while flagging
  // the form as "saving" the moment an edit is scheduled, so the gap before the
  // debounced PATCH actually fires is also treated as having unsaved changes.
  const handleUpdateWithDebounce = (
    values: Parameters<typeof debouncedUpdate>[0]
  ) => {
    setIsDebouncedSavePending(true);
    debouncedUpdate(values);
  };

  const handleUpdate = (values: BookkeepingDocumentMetadataDto) => {
    // Track this PATCH as in flight (counted here, not only in the debounced
    // wrapper, so direct non-debounced calls are covered too). Counting rather
    // than a boolean means overlapping saves each clear their own slot, so
    // booking stays disabled until the last one settles.
    setPendingSaveCount((count) => count + 1);

    const { bobContactId, referenceId, ...body } = values;

    let dueDate = null;
    let issueDate = null;

    if (body?.dueDate) {
      dueDate = body?.dueDate;
    }

    if (body?.issueDate) {
      issueDate = body?.issueDate;
    }

    if (
      body?.dueDate === undefined &&
      body?.dueDate !== null &&
      formValues?.dueDate
    ) {
      dueDate = formValues?.dueDate;
    }

    if (
      body?.issueDate === undefined &&
      body?.issueDate !== null &&
      formValues?.issueDate
    ) {
      issueDate = formValues?.issueDate;
    }

    updateDocument(
      {
        parameter: reqParameters,
        requestBody: {
          bobContactId: bobContactId,
          referenceId: referenceId,
          bookeepingMetadata: {
            ...omit(form.getFieldsValue(), ['bobContactId', 'referenceId']),
            ...body,
            dueDate:
              dueDate !== null ? dayjs(dueDate).format('YYYY-MM-DD') : null,
            issueDate:
              issueDate !== null ? dayjs(issueDate).format('YYYY-MM-DD') : null,
          } as Schemas.BookkeepingDocumentMetadataDto,
        },
      },
      {
        onSuccess: (updatedDoc: Schemas.Document) => {
          showUpdatedMessage();
          updateFormFields(updatedDoc);
          // This save settled: release its in-flight slot. Once the count hits
          // zero (no other save pending) booking is re-enabled.
          setPendingSaveCount((count) => Math.max(0, count - 1));
        },
        onError: () => {
          message.error('Failed to save document changes');
          // A failed request is no longer "saving", so release its slot too —
          // otherwise booking would stay blocked with no recovery path until an
          // unrelated save happens to succeed. The operator has been shown the
          // error and can retry the edit.
          setPendingSaveCount((count) => Math.max(0, count - 1));
        },
      }
    );
  };

  const handleSubmit = () =>
    updateDocument(
      {
        parameter: reqParameters,
        requestBody: {
          adminStatus: 'processing',
          bobStatus: 'pending',
        },
      },
      {
        onSuccess: () => {
          showUpdatedMessage();
          setUnlockedDocumentId(false);
          onAfterUpdate();
        },
      }
    );

  const handleLeaveProcessing = () => {
    updateDocument(
      {
        parameter: reqParameters,
        requestBody: {
          adminStatus: 'ready',
        },
      },
      {
        onSuccess: () => {
          message.success(
            isQueuedForBob
              ? 'Document removed from the BOB queue'
              : 'Document removed from manual booking'
          );
          onAfterUpdate();
        },
      }
    );
  };

  const handleRetryBobSync = () => {
    retryBobSync(
      { parameter: reqParameters },
      {
        onSuccess: () => {
          message.success('The document is back in the BOB queue');
          onAfterUpdate();
        },
        // The endpoint answers 409 ALREADY_BOOKED for a document BOB50 already holds. The button is
        // disabled for that case, so reaching it here means the row changed underneath us — say so
        // rather than leave the operator believing a retry is running.
        onError: (error) => {
          captureException(error);
          message.error(
            'Could not retry the BOB sync. Reload the document and check its Doc ID.'
          );
        },
      }
    );
  };

  /**
   * The Doc ID is optional here, and the confirmation has to say which of the two is about to happen.
   *
   * A document can be booked by hand before its BOB50 number is known, so an empty field is a real
   * case rather than a mistake — but the standing copy promised "the Doc ID currently in the form is
   * saved with it", which describes nothing at all when the form is empty. Worse, the id is what the
   * retry guard keys on: without one, nothing in the row says BOB50 already holds this document.
   */
  const markAsBookedWarning = (() => {
    const referenceId = String(values?.referenceId ?? '').trim();

    return referenceId
      ? `Mark this document as booked in BOB50 as ${referenceId}? The Doc ID is saved with it.`
      : 'Mark this document as manually booked with no Doc ID? Nothing will record which BOB50 booking it is, so it cannot be reconciled against BOB50 later. Enter the Doc ID first if you have it.';
  })();

  const handleMarkAsBooked = () => {
    // The Doc ID travels with the status change, in one request. Sending it on its own is refused
    // while the document is in the queue — a booking id there is exactly the shape that leads to a
    // second booking in BOB50 — so the two halves of "I booked this by hand" have to be one write.
    const referenceId = String(form.getFieldValue('referenceId') ?? '').trim();

    updateDocument(
      {
        parameter: reqParameters,
        requestBody: {
          adminStatus: 'processed',
          ...(referenceId ? { referenceId } : {}),
        },
      },
      {
        onSuccess: () => {
          message.success('Document marked as manually booked');
          onAfterUpdate();
        },
        onError: () => {
          message.error('Failed to mark the document as manually booked');
        },
      }
    );
  };

  const handleUpdateList = (
    field: string,
    name: number,
    value: string | number | boolean | null
  ) =>
    ((form.getFieldValue('items') || []) as Schemas.BookingItemDto[]).map(
      (item, index) => (name === index ? { ...item, [field]: value } : item)
    );

  const handleUpdateItemTaxFieldsbyIndex = (
    index: number,
    itemsToUpdate: Schemas.BookingItemDto[]
  ) => {
    itemsToUpdate = updateItemTaxAmountByIndex(index, itemsToUpdate, vats);
    itemsToUpdate = updateItemTotalPriceByIndex(index, itemsToUpdate);

    const currency = form.getFieldValue('currency');
    itemsToUpdate = updateItemEurTaxAmountByIndex(
      index,
      itemsToUpdate,
      currency,
      currencyExchangeRate
    );
    itemsToUpdate = updateEurTotalPriceByIndex(index, itemsToUpdate);

    handleUpdateWithDebounce({ items: itemsToUpdate });
  };

  const handleUpdateRelatedFieldsToNetPriceByIndex = (
    index: number,
    itemsToUpdate: Schemas.BookingItemDto[]
  ) => {
    itemsToUpdate = updateItemTaxAmountByIndex(index, itemsToUpdate, vats);
    itemsToUpdate = updateItemTotalPriceByIndex(index, itemsToUpdate);

    const currency = form.getFieldValue('currency');
    itemsToUpdate = updateItemEurNetPriceByIndex(
      index,
      itemsToUpdate,
      currency,
      currencyExchangeRate
    );
    itemsToUpdate = updateItemEurTaxAmountByIndex(
      index,
      itemsToUpdate,
      currency,
      currencyExchangeRate
    );
    itemsToUpdate = updateEurTotalPriceByIndex(index, itemsToUpdate);

    handleUpdateWithDebounce({ items: itemsToUpdate });
  };

  const handleUpdateRelatedFieldsToTaxAmountByIndex = (
    index: number,
    itemsToUpdate: Schemas.BookingItemDto[]
  ) => {
    itemsToUpdate = updateItemTotalPriceByIndex(index, itemsToUpdate);

    const currency = form.getFieldValue('currency');
    itemsToUpdate = updateItemEurTaxAmountByIndex(
      index,
      itemsToUpdate,
      currency,
      currencyExchangeRate
    );

    itemsToUpdate = updateEurTotalPriceByIndex(index, itemsToUpdate);

    handleUpdateWithDebounce({ items: itemsToUpdate });
  };

  const handleUpdateItemEurTotalPriceByIndex = (
    index: number,
    itemsToUpdate: Schemas.BookingItemDto[]
  ) => {
    const updatedItems = updateEurTotalPriceByIndex(index, itemsToUpdate);
    handleUpdateWithDebounce({ items: updatedItems });
  };

  const handleUpdateContact = async (contact: Schemas.BobContact) => {
    if (form.getFieldValue('bobContactId') !== contact.id) {
      setSelectedContact(contact);
      form.setFieldValue('bobContactId', contact.id);

      await new Promise((resolve) => setTimeout(resolve, 0));

      const result = await refetch();

      setParamsForContact(result.data);
    }
  };

  const hasDisabledFormSubmit = () => {
    if (!hasBobReferenceId) {
      return true;
    }

    if (currencyExistsAndIsNotDefault && !currencyExchangeRate) {
      return true;
    }

    if (!form.getFieldValue('items')?.length) return true;

    if (!getStatusByAmount(formValues).isValid) return true;

    if (
      currencyExistsAndIsNotDefault &&
      !getStatusByEurAmount(formValues).isValid
    ) {
      return true;
    }

    if (!submittable) return true;

    return false;
  };

  const toggleFieldLock = (
    fieldType: keyof UnlockedFieldsType,
    index: number
  ) => {
    setUnlockedItemsFields((prev) => ({
      ...prev,
      [fieldType]: {
        ...prev[fieldType],
        [index]: !prev[fieldType]?.[index],
      },
    }));
  };

  return (
    <Spin
      tip="We are uploading the data, please check it as soon as it is uploaded"
      size="large"
      spinning={isLoadingParamsForContact}
    >
      {hasBookingError && (
        <S.ErrorMessage>
          There was an error during document booking:
          <br />"{document.adminNotes}".
          <br />
          <br />
          Check the filled data carefully and try again.
          <br />
          <br />
          If the problem still persists:
          <br />
          1. Book the document manually in BOB50.
          <br />
          2. Mark it as booked in the documents list.
          <br />
          3. Add the 'Doc Id' based on BOB50 document number to this document in
          the documents list.
        </S.ErrorMessage>
      )}

      {canLeaveProcessing && (
        <Form.Item noStyle>
          <S.ActionRow justify="space-between" gap={12}>
            <DocumentExpenseActionButtonWithConfirmation
              buttonText={
                isQueuedForBob
                  ? 'Remove from the BOB queue'
                  : 'Start booking automatically instead'
              }
              buttonType="primary"
              modalTitle={
                isQueuedForBob
                  ? 'Remove from the BOB queue'
                  : 'Start booking automatically instead of manually'
              }
              modalText={
                isQueuedForBob
                  ? 'The document goes back to New, so its data can be corrected before another attempt. Nothing is sent to BOB50 in the meantime.'
                  : 'Are you sure you want to start booking this document via automatic booking?'
              }
              onConfirm={handleLeaveProcessing}
            />
            <DocumentExpenseActionButtonWithConfirmation
              buttonText="Mark as manually booked"
              buttonType="primary-danger"
              modalTitle="Mark as manually booked"
              modalText={markAsBookedWarning}
              // The Doc ID travels with this request, so it must be the saved one: a debounced save
              // still in flight would land afterwards and overwrite what we just sent.
              isDisabled={isSavingChanges}
              onConfirm={handleMarkAsBooked}
            />
          </S.ActionRow>
        </Form.Item>
      )}

      {retry && (
        <Form.Item noStyle>
          <S.ActionRow gap={12}>
            <Tooltip title={retry.blockedReason ?? ''}>
              <span>
                <DocumentExpenseActionButtonWithConfirmation
                  buttonText="Retry BOB sync"
                  buttonType="primary"
                  modalTitle="Retry the BOB sync"
                  modalText="The document goes back into the BOB queue with its error cleared and the stuck timer restarted. Nothing is sent to BOB50 until the connector picks it up."
                  isDisabled={!retry.allowed || isRetrying || isSavingChanges}
                  onConfirm={handleRetryBobSync}
                />
              </span>
            </Tooltip>
          </S.ActionRow>
        </Form.Item>
      )}

      {!hasBobReferenceId && (
        <S.ErrorMessage>
          Automatic booking is disabled for this company.
          <br />
          <br />
          Please follow these steps:
          <br />
          1. Book the document manually in BOB50.
          <br />
          2. Mark it as booked in the documents list.
          <br />
          3. Add the 'Doc Id' to this document based on BOB50 document number.
        </S.ErrorMessage>
      )}

      {document?.documentMetadata?.description &&
        document?.documentMetadata?.description !==
          document?.bookeepingMetadata?.description && (
          <S.NoteMessage>
            ❗️ Client's custom invoice description:
            <br />
            <br />
            {document?.documentMetadata?.description}
          </S.NoteMessage>
        )}

      <Form
        form={form}
        onFinish={handleSubmit}
        onValuesChange={(_, values) => setFormValues(values)}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          items: [],
        }}
      >
        <Row gutter={[12, 0]}>
          <DocumentExpenseWrapperFormControl
            type="input"
            span={12}
            form={{
              label: 'Number',
              name: 'number',
              rules: [{ required: true }],
            }}
            control={{
              disabled: hasDisabled,
              onChange: (e) =>
                handleUpdateWithDebounce({
                  number: e.target.value,
                }),
            }}
          />

          <DocumentExpenseWrapperFormControl
            type="input"
            span={12}
            form={{
              label: 'Doc ID',
              name: 'referenceId',
            }}
            control={{
              addonAfter:
                hasDisabled || unlockedDocumentId ? (
                  <Button
                    type="link"
                    icon={<IconLock width={20} height={20} />}
                    onClick={() =>
                      setUnlockedDocumentId((prevState) => !prevState)
                    }
                  />
                ) : null,
              disabled: !isReady ? !unlockedDocumentId && hasDisabled : true,
              onChange: (e) =>
                handleUpdateWithDebounce({
                  referenceId: e.target.value,
                }),
            }}
          />

          <DocumentExpenseWrapperFormControl
            type="date-picker"
            span={12}
            form={{
              label: 'Issue date',
              name: 'issueDate',
              rules: [{ required: true }],
            }}
            control={{
              format: DEFAULT_DATE_FORMAT,
              disabled: hasDisabled,
              // NOTE: When the currency is not EUR and not empty, the currency exchange rates loading and we update the related fields in [currencyExchangeRate] useEffect.
              // It is IMPORTANT to update everything in one place because if we would update issueDate here, and also update the fields in useEffect, then issueDate might update later and overwrite the items currency-related field values from useEffect.
              onChange: (value) => {
                const currency = form.getFieldValue('currency');

                const isCurrencyDefaultOrEmpty =
                  currency === DEFAULT_CURRENCY_CODE ||
                  currency === null ||
                  currency === undefined;
                const isIssueDateEmpty = value === null || value === undefined;

                if (isCurrencyDefaultOrEmpty || isIssueDateEmpty) {
                  // NOTE: the [currencyExchangeRate] useEffect is not triggered
                  handleUpdate({
                    issueDate: value && dayjs(value).format('YYYY-MM-DD'),
                  });
                }
              },
            }}
          />

          <DocumentExpenseWrapperFormControl
            type="date-picker"
            span={12}
            form={{
              label: 'Due date',
              name: 'dueDate',
            }}
            control={{
              format: DEFAULT_DATE_FORMAT,
              disabled: hasDisabled,
              onChange: (value) =>
                handleUpdate({
                  dueDate: value && dayjs(value).format('YYYY-MM-DD'),
                }),
            }}
          />
        </Row>

        <ContactsPopupList
          placement="bottomLeft"
          onSelect={handleUpdateContact}
          onAfterCreate={handleUpdateContact}
          emptyText="Contact not found. Add it here and it will be created in BOB50 on the next sync cycle."
          {...(hasDisabled && { open: false })}
        >
          <Form.Item
            label={
              <Flex gap={4} vertical>
                <span>Contact</span>
                <S.ExtraText>
                  Select a contact and the information will be pulled from BOB50
                </S.ExtraText>
              </Flex>
            }
            name="bobContactId"
            rules={[{ required: true }]}
          >
            <S.SelectContacts
              size="large"
              options={[
                {
                  label: selectedContact?.name,
                  value: selectedContact?.id,
                },
              ]}
              disabled={hasDisabled}
              popupClassName="popup-hidden"
            />
          </Form.Item>
        </ContactsPopupList>

        <Row gutter={[12, 0]}>
          <DocumentExpenseWrapperFormControl
            type="select"
            span={24}
            form={{
              label: 'Journal',
              name: 'daybookId',
              rules: [{ required: true }],
            }}
            control={{
              options: (daybooks || []).map((book) => ({
                label: `${book.id} - ${book.name}`,
                value: book.id,
              })),
              loading: isLoadingDaybooks,
              disabled: hasDisabled,
              onChange: (value) =>
                handleUpdate({
                  daybookId: value,
                }),
            }}
          />
        </Row>

        <Row gutter={[12, 0]}>
          <DocumentExpenseWrapperFormControl
            type="select"
            span={currencyExistsAndIsNotDefault ? 12 : 24}
            form={{
              label: 'Currency',
              name: 'currency',
              rules: [{ required: true }],
            }}
            control={{
              options: currencyCodes.map((code) => ({
                label: code,
                value: code,
              })),
              disabled: hasDisabled,
              onChange: (value) => {
                // NOTE: When the currency is not EUR and the issueDate is not empty, the currency exchange rates loading and we update the related fields in [currencyExchangeRate] useEffect.
                // It is IMPORTANT to update everything in one place because if we would update currency here, and also update the fields in useEffect, then currency might update later and overwrite the items currency-related field values from useEffect.
                const issueDate = form.getFieldValue('issueDate');
                const isIssueDateEmpty =
                  issueDate === null || issueDate === undefined;
                const isCurrencyDefaultOrEmpty =
                  value === DEFAULT_CURRENCY_CODE ||
                  value === null ||
                  value === undefined;
                if (isCurrencyDefaultOrEmpty || isIssueDateEmpty) {
                  // NOTE: the [currencyExchangeRate] useEffect is not triggered
                  let updateValues: BookkeepingDocumentMetadataDto = {
                    currency: value,
                  };
                  const items = form.getFieldValue('items') || [];
                  updateValues = resetEurValues(updateValues, items);
                  handleUpdate(updateValues);
                }
              },
            }}
          />

          {currencyExistsAndIsNotDefault && (
            <DocumentExpenseWrapperFormControl
              type="input-number"
              span={12}
              form={{
                label: 'Exchange rate',
                name: 'fromEurRate',
                rules: [{ required: true }],
              }}
              control={{
                min: 0,
                disabled: true,
              }}
            />
          )}
        </Row>

        {currencyExistsAndIsNotDefault &&
          !isLoadingCurrencyExchangeRate &&
          form.getFieldValue('issueDate') &&
          !currencyExchangeRate && (
            <Flex style={{ margin: '18px 0' }}>
              <DocumentExpenseAmountStatus
                isEqual={!!currencyExchangeRate}
                label={
                  <>
                    <p>
                      The exchange rate and related amounts were not calculated.
                    </p>
                    <p>
                      If the invoice date is today, check the document tomorrow;
                    </p>
                    <p>
                      If the invoice date is not today, write to the support
                      team (#accounting in Slack)
                    </p>
                  </>
                }
              />
            </Flex>
          )}

        <Row gutter={[12, 0]}>
          <DocumentExpenseWrapperFormControl
            type="input-number"
            span={currencyExistsAndIsNotDefault ? 12 : 24}
            form={{
              label: 'Total Amount',
              name: 'amount',
              rules: [{ required: true }],
            }}
            control={{
              min: 0,
              disabled: hasDisabled,
              onChange: (value) => {
                const currency = form.getFieldValue('currency');
                const numericValue = value as number | null;
                const updateData: BookkeepingDocumentMetadataDto = {
                  amount: numericValue,
                };
                updateData.eurAmount = calculateDocumentEurAmount(
                  numericValue,
                  currency,
                  currencyExchangeRate
                );
                handleUpdateWithDebounce(updateData);
              },
            }}
          />

          {currencyExistsAndIsNotDefault && (
            <DocumentExpenseWrapperFormControl
              type="input-number"
              span={12}
              form={{
                label: 'Total Amount in EUR',
                name: 'eurAmount',
                rules: [{ required: true }],
              }}
              control={{
                min: 0,
                addonAfter: !hasDisabled ? (
                  <Button
                    type="link"
                    icon={<IconEdit width={20} height={20} />}
                    onClick={() => setUnlockedEurAmount((prev) => !prev)}
                  />
                ) : null,
                disabled: hasDisabled || !unlockedEurAmount,
                onChange: (value) =>
                  handleUpdateWithDebounce({
                    eurAmount: value,
                    fromEurRate: currencyExchangeRate,
                  }),
              }}
            />
          )}
        </Row>

        <Flex gap={12} vertical>
          <Form.List name="items">
            {(items, { add, remove }) => (
              <>
                <S.LinesTitle>Lines</S.LinesTitle>

                {items.map(({ key, name, ...rest }) => (
                  <S.Card key={key} gap={6} vertical>
                    <Flex align="flex-start" gap={8} justify="space-between">
                      <DocumentExpenseWrapperFormControl
                        type="textarea"
                        form={{
                          name: [name, 'name'],
                          rules: [
                            { required: true, message: '' },
                            {
                              validator: (_, value) =>
                                value && value.length > 40
                                  ? Promise.reject(
                                      new Error(
                                        'You have exceeded the maximum number of characters.'
                                      )
                                    )
                                  : Promise.resolve(),
                            },
                          ],
                        }}
                        control={{
                          rows: 2,
                          showCount: true,
                          maxLength: 40,
                          disabled: hasDisabled,
                          onChange: (e) =>
                            handleUpdateWithDebounce({
                              items: handleUpdateList(
                                'name',
                                name,
                                e.target.value
                              ),
                            }),
                        }}
                      />

                      <Button
                        type="link"
                        icon={<IconTrash width={24} height={24} />}
                        onClick={() => {
                          handleUpdate({
                            items: (formValues?.items || []).filter(
                              (_, index) => name !== index
                            ),
                          });

                          remove(name);
                        }}
                        disabled={hasDisabled}
                        danger
                      />
                    </Flex>

                    <Row gutter={[12, 6]}>
                      <DocumentExpenseWrapperFormControl
                        type="select"
                        span={24}
                        form={{
                          label: 'Account code',
                          name: [name, 'accountCode'],
                          rules: [{ required: true }],
                          ...rest,
                        }}
                        control={{
                          placeholder: 'Account code',
                          size: 'middle',
                          options: (accounts || []).map((account) => ({
                            label: `${account.id} - ${account.name}`,
                            value: account.id,
                          })),
                          disabled: hasDisabled,
                          loading: isLoadingAccounts,
                          onChange: (value) =>
                            handleUpdate({
                              items: handleUpdateList(
                                'accountCode',
                                name,
                                value
                              ),
                            }),
                          showSearch: true,
                          optionFilterProp: 'label',
                        }}
                      />

                      <DocumentExpenseWrapperFormControl
                        type="select"
                        span={24}
                        form={{
                          label: 'VAT code',
                          name: [name, 'vatCode'],
                          rules: [{ required: true }],
                          ...rest,
                        }}
                        control={{
                          placeholder: 'VAT code',
                          size: 'middle',
                          options: (vats || []).map((vat) => ({
                            label: `${vat.id} - ${vat.name}`,
                            value: vat.id,
                          })),
                          disabled: hasDisabled,
                          loading: isLoadingVats,
                          showSearch: true,
                          optionFilterProp: 'label',
                          onChange: (value) =>
                            handleUpdateItemTaxFieldsbyIndex(
                              name,
                              handleUpdateList('vatCode', name, value)
                            ),
                        }}
                      />

                      <DocumentExpenseWrapperFormControl
                        type="checkbox"
                        span={24}
                        form={{
                          name: [name, 'isEffectiveVatZero'],
                          valuePropName: 'checked',
                          ...rest,
                        }}
                        control={{
                          disabled: hasDisabled,
                          onChange: (e) =>
                            handleUpdateItemTaxFieldsbyIndex(
                              name,
                              handleUpdateList(
                                'isEffectiveVatZero',
                                name,
                                e.target.checked
                              )
                            ),
                          children: (
                            <Typography.Text strong>
                              Effective VAT will be 0%
                            </Typography.Text>
                          ),
                        }}
                      />

                      <DocumentExpenseWrapperFormControl
                        type="input-number"
                        span={currencyExistsAndIsNotDefault ? 12 : 24}
                        form={{
                          label: 'Amount excluding VAT',
                          name: [name, 'netPrice'],
                          rules: [{ required: true }],
                          ...rest,
                        }}
                        control={{
                          min: 0,
                          size: 'middle',
                          disabled: hasDisabled,
                          onChange: (value) =>
                            handleUpdateRelatedFieldsToNetPriceByIndex(
                              name,
                              handleUpdateList('netPrice', name, value)
                            ),
                        }}
                      />

                      {currencyExistsAndIsNotDefault && (
                        <DocumentExpenseWrapperFormControl
                          type="input-number"
                          span={12}
                          form={{
                            label: 'Amount excluding VAT in EUR',
                            name: [name, 'eurNetPrice'],
                            ...rest,
                          }}
                          control={{
                            min: 0,
                            size: 'middle',
                            addonAfter: !hasDisabled ? (
                              <Button
                                type="link"
                                icon={<IconEdit width={20} height={20} />}
                                onClick={() =>
                                  toggleFieldLock('eurNetPrice', name)
                                }
                              />
                            ) : null,
                            disabled:
                              hasDisabled ||
                              !unlockedItemsFields.eurNetPrice?.[name],
                            onChange: (value) =>
                              handleUpdateItemEurTotalPriceByIndex(
                                name,
                                handleUpdateList('eurNetPrice', name, value)
                              ),
                          }}
                        />
                      )}

                      <DocumentExpenseWrapperFormControl
                        type="input-number"
                        span={currencyExistsAndIsNotDefault ? 12 : 24}
                        form={{
                          label: 'VAT amount',
                          name: [name, 'taxAmount'],
                          rules: [{ required: true }],
                          ...rest,
                        }}
                        control={{
                          min: 0,
                          size: 'middle',
                          addonAfter: !hasDisabled ? (
                            <Button
                              type="link"
                              icon={<IconEdit width={20} height={20} />}
                              onClick={() => toggleFieldLock('taxAmount', name)}
                            />
                          ) : null,
                          disabled:
                            hasDisabled ||
                            !unlockedItemsFields.taxAmount?.[name],
                          addonBefore: (() => {
                            const item = form.getFieldValue(['items', name]);
                            if (item?.isEffectiveVatZero) {
                              return (
                                <Typography.Text type="success">
                                  Eff.: 0.00
                                </Typography.Text>
                              );
                            }
                            return null;
                          })(),
                          onChange: (value) =>
                            handleUpdateRelatedFieldsToTaxAmountByIndex(
                              name,
                              handleUpdateList('taxAmount', name, value)
                            ),
                        }}
                      />

                      {currencyExistsAndIsNotDefault && (
                        <DocumentExpenseWrapperFormControl
                          type="input-number"
                          span={12}
                          form={{
                            label: 'VAT amount in EUR',
                            name: [name, 'eurTaxAmount'],
                            ...rest,
                          }}
                          control={{
                            min: 0,
                            size: 'middle',
                            addonAfter: !hasDisabled ? (
                              <Button
                                type="link"
                                icon={<IconEdit width={20} height={20} />}
                                onClick={() =>
                                  toggleFieldLock('eurTaxAmount', name)
                                }
                              />
                            ) : null,
                            addonBefore: (() => {
                              const item = form.getFieldValue(['items', name]);
                              if (item?.isEffectiveVatZero) {
                                return (
                                  <Typography.Text type="success">
                                    Eff.: 0.00
                                  </Typography.Text>
                                );
                              }
                              return null;
                            })(),
                            disabled:
                              hasDisabled ||
                              !unlockedItemsFields.eurTaxAmount?.[name],
                            onChange: (value) =>
                              handleUpdateItemEurTotalPriceByIndex(
                                name,
                                handleUpdateList('eurTaxAmount', name, value)
                              ),
                          }}
                        />
                      )}

                      <Divider style={{ margin: '6px 0' }} />

                      <DocumentExpenseWrapperFormControl
                        type="input-number"
                        span={currencyExistsAndIsNotDefault ? 12 : 24}
                        form={{
                          label: 'Total including eff. VAT',
                          name: [name, 'totalPrice'],
                          rules: [{ required: true }],
                          ...rest,
                        }}
                        control={{
                          min: 0,
                          size: 'middle',
                          disabled: true,
                        }}
                      />

                      {currencyExistsAndIsNotDefault && (
                        <DocumentExpenseWrapperFormControl
                          type="input-number"
                          span={12}
                          form={{
                            label: 'Total including eff. VAT in EUR',
                            name: [name, 'eurTotalPrice'],
                            rules: [{ required: true }],
                            ...rest,
                          }}
                          control={{
                            min: 0,
                            size: 'middle',
                            disabled: true,
                          }}
                        />
                      )}
                    </Row>
                  </S.Card>
                ))}

                <S.LinesAdd
                  disabled={hasDisabled}
                  onClick={() => {
                    handleUpdate({
                      items: [
                        ...(formValues?.items || []),
                        { name: '' } as Schemas.BookingItem,
                      ],
                    });

                    add();
                  }}
                >
                  + Add line
                </S.LinesAdd>
              </>
            )}
          </Form.List>
        </Flex>

        <S.Submit
          align="center"
          justify={formValues?.amount ? 'space-between' : 'flex-end'}
        >
          <Flex gap={24} justify="space-between" flex="1">
            <Flex gap={16} vertical>
              {!submittable ? (
                <DocumentExpenseAmountStatus
                  isEqual={false}
                  label="Not all fields are filled in"
                />
              ) : form.getFieldValue('items')?.length ? (
                <>
                  <DocumentExpenseAmountStatus
                    isEqual={getStatusByAmount(formValues).isValid}
                    label={
                      getStatusByAmount(formValues).isValid
                        ? 'Total amount matches items amount including VAT'
                        : `Total amount (${getStatusByAmount(formValues).total || 0}) does not match items amount including VAT (${getStatusByAmount(formValues).itemsTotal || 0})`
                    }
                  />
                  {currencyExistsAndIsNotDefault && (
                    <DocumentExpenseAmountStatus
                      isEqual={getStatusByEurAmount(formValues).isValid}
                      label={
                        getStatusByEurAmount(formValues).isValid
                          ? 'Total EUR amount matches items EUR amount including VAT'
                          : `Total EUR amount (${getStatusByEurAmount(formValues).total || 0}) does not match items EUR amount including VAT (${getStatusByEurAmount(formValues).itemsTotal || 0})`
                      }
                    />
                  )}
                </>
              ) : !form.getFieldValue('items')?.length ? (
                <DocumentExpenseAmountStatus
                  isEqual={false}
                  label="No added lines"
                />
              ) : (
                <div />
              )}
            </Flex>

            {!hasDisabled && hasBobReferenceId && (
              <Form.Item noStyle>
                <Flex justify="flex-end">
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    disabled={hasDisabledFormSubmit()}
                  >
                    Submit for booking
                  </Button>
                </Flex>
              </Form.Item>
            )}
          </Flex>
        </S.Submit>
      </Form>
    </Spin>
  );
};

export default DocumentExpenseForm;
